import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faExclamationTriangle,
  faPen,
  faPhone,
  faPlus,
  faSterlingSign,
  faUser,
} from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { faChartLine } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { faCircleInfo, faFlag, faInbox } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import { Badge } from "@/components/ui/badge/Badge";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { useSetWindowTitle } from "@/components/ui/os-layout";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import type {
  FaClientDetail,
  FaClientUpdate,
  TaskActionItem,
  VisibilityScope,
} from "@/services/api/agentic-v2";
import { VisibilityFilter } from "@/components/agentic-v2/VisibilityFilter";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import { useClientDetail } from "@/services/api/agentic-v2/queries/useClientDetail";
import { useClientTasks } from "@/services/api/agentic-v2/queries/useClientTasks";
import { ClientAvatar } from "@/components/clients/ClientAvatar";
import { ClientArtifactsSection } from "@/components/clients/ClientArtifactsSection";
import { TaskReviewTable } from "@/components/clients/TaskReviewTable";
import {
  compactJourneyStageLabel,
  journeyStageBadgeColor,
} from "@/components/clients/clientStageDisplay";
import { useAppLocation } from "@/navigation/appLocation";
import { leadDetailCodec } from "@/navigation/apps/leadDetailLocation";
import { type LeadDetailTabId } from "@/components/leads/leadDetailTabs";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { LeadEmailDialog, buildLeadMailto } from "@/components/leads/LeadEmailDialog";
import { RecordLostDialog } from "@/components/leads/RecordLostDialog";
import FormInput from "@/components/ui/form-input";

/**
 * Lead record page for the Leads module.
 *
 * Simplified lead-first layout (not the full client workspace):
 *   Overview | Tasks | Artefacts
 *
 * Detail, tasks, and artefacts all reuse the existing tenant-scoped
 * fa-client queries — leads live in the same collection.
 */
export interface LeadDetailProps {
  faClientId: string;
}

export function LeadDetail({ faClientId }: LeadDetailProps) {
  const setWindowTitle = useSetWindowTitle();
  const isWindowLive = useWindowLive();

  const [location, setLocation] = useAppLocation(leadDetailCodec);
  const [taskVisibility, setTaskVisibility] = useState<VisibilityScope>("all");

  const { client, loading, error, refresh } = useClientDetail({ faClientId });
  const visibleTab: LeadDetailTabId = location.tab ?? "overview";

  const tasks = useClientTasks({
    faClientId,
    visibility: taskVisibility,
    enabled: isWindowLive,
  });

  const tabs = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      {
        id: "tasks",
        label: "Tasks",
        // Prefer the loaded list count so the badge matches the Tasks tab;
        // fall back to the detail payload while tasks are still loading.
        count: (tasks.loading ? client?.open_task_count : tasks.tasks.length) || undefined,
      },
      { id: "artefacts", label: "Artefacts" },
    ],
    [client?.open_task_count, tasks.loading, tasks.tasks.length],
  );

  const titleSetRef = useRef(false);
  useEffect(() => {
    if (titleSetRef.current || !client) return;
    setWindowTitle(client.display_name);
    titleSetRef.current = true;
  }, [client, setWindowTitle]);

  return (
    <div
      className={clsx("flex h-full w-full gap-4 p-4", WORKBENCH_ROOT_TEXT_CLASS)}
      data-testid="lead-detail"
    >
      <div
        className={clsx(
          "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl",
          WORKBENCH_SURFACE_CLASS,
        )}
      >
        {loading ? (
          <div className="p-6">
            <LoadingPlaceholder height="120px" />
          </div>
        ) : error || !client ? (
          <ErrorState message={error?.message ?? "Lead not found"} onRetry={refresh} />
        ) : (
          <>
            <div className="shrink-0 px-6 pb-4 pt-5">
              <Header client={client} faClientId={faClientId} onChanged={refresh} />
            </div>

            <div className="shrink-0 overflow-x-auto px-6">
              <SegmentedTabs
                tabs={tabs}
                activeId={visibleTab}
                onChange={(id) => setLocation({ tab: id as LeadDetailTabId })}
              />
            </div>

            <div
              className={clsx(
                "min-h-0 flex-1 px-6 py-5",
                visibleTab === "tasks"
                  ? "flex flex-col overflow-hidden"
                  : "overflow-y-auto",
              )}
            >
              {visibleTab === "overview" && (
                <OverviewTab client={client} faClientId={faClientId} onChanged={refresh} />
              )}
              {visibleTab === "tasks" && (
                <TasksTab
                  tasks={tasks.tasks}
                  loading={tasks.loading}
                  visibility={taskVisibility}
                  onVisibilityChange={setTaskVisibility}
                />
              )}
              {visibleTab === "artefacts" && (
                <ClientArtifactsSection faClientId={faClientId} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────────

function Header({ client, faClientId, onChanged }: { client: FaClientDetail; faClientId: string; onChanged: () => void }) {
  const lead = client.lead;
  const contact = (client.contact ?? {}) as Record<string, unknown>;
  const capture = (lead?.capture_fields ?? {}) as Record<string, unknown>;
  const email =
    firstString(contact, ["email", "email_address"]) ||
    firstString(capture, ["email_address", "email"]);
  const phone =
    firstString(contact, [
      "phone_mobile",
      "mobile",
      "mobile_telephone",
      "phone",
      "phone_home",
      "home_telephone",
    ]) || firstString(capture, ["mobile_telephone", "home_telephone", "phone"]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [calling, setCalling] = useState(false);
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(client.display_name);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (!nameEditing) setNameDraft(client.display_name);
  }, [client.display_name, nameEditing]);

  const handleNameSave = async () => {
    const trimmed = nameDraft.trim();
    if (trimmed === "") {
      setNameError("Name must not be blank.");
      return;
    }
    if (trimmed === client.display_name) {
      setNameEditing(false);
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      await AgenticV2API.updateFaClient(faClientId, { display_name: trimmed });
      onChanged();
      setNameEditing(false);
    } catch (err: unknown) {
      setNameError(err instanceof Error ? err.message : "Couldn't save the name.");
    } finally {
      setNameSaving(false);
    }
  };

  const handleEmail = async (event: React.MouseEvent) => {
    // Pre-flight like the call button: open the compose dialog only when we
    // can actually send; otherwise go straight to the mail app (this also
    // covers stale pages whose lead no longer exists).
    event.preventDefault();
    if (!email) return;
    try {
      const status = await AgenticV2API.getLeadEmailStatus(faClientId);
      if (status.can_send) {
        setEmailOpen(true);
        return;
      }
    } catch {
      // fall through to the mail app below
    }
    window.open(buildLeadMailto(email, "", ""), "_self");
  };

  const handleCall = async (event: React.MouseEvent) => {
    // Prefer the booked meeting's video link (same as the meeting Join flow);
    // fall back to the phone link when there is no meeting yet.
    event.preventDefault();
    if (calling) return;
    setCalling(true);
    try {
      const link = await AgenticV2API.getLeadCallLink(faClientId);
      if (link.join_url) {
        window.open(link.join_url, "_blank", "noopener,noreferrer");
        return;
      }
    } catch {
      // fall through to the phone link below
    } finally {
      setCalling(false);
    }
    if (phone) window.open(`tel:${phone.replace(/[\s()-]/g, "")}`, "_self");
  };

  return (
    <div className="flex items-start gap-4">
      <div className="shrink-0 rounded-2xl ring-1 ring-slate-950/10 dark:ring-white/15">
        <ClientAvatar name={client.display_name} size="md" />
      </div>
      <div className="min-w-0 flex-1">
        {nameEditing ? (
          <div>
            <div className="flex items-center gap-2">
              <FormInput
                value={nameDraft}
                onChange={(event) => setNameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void handleNameSave();
                  if (event.key === "Escape") setNameEditing(false);
                }}
                disabled={nameSaving}
                aria-label="Lead display name"
                data-testid="lead-name-input"
                autoFocus
              />
              <Button size="sm" onClick={() => void handleNameSave()} disabled={nameSaving} loading={nameSaving} data-testid="lead-name-save">
                Save
              </Button>
              <Button outline size="sm" onClick={() => setNameEditing(false)} disabled={nameSaving}>
                Cancel
              </Button>
            </div>
            {nameError ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid="lead-name-error">
                {nameError}
              </p>
            ) : null}
          </div>
        ) : (
          <h1 className="group flex items-center gap-2 truncate text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
            <span className="truncate" data-testid="lead-display-name">{client.display_name}</span>
            <button
              type="button"
              onClick={() => {
                setNameDraft(client.display_name);
                setNameError(null);
                setNameEditing(true);
              }}
              aria-label="Edit lead name"
              data-testid="lead-name-edit"
              className="shrink-0 rounded p-1 text-slate-500 opacity-0 transition-opacity hover:text-slate-700 focus:opacity-100 group-hover:opacity-100 dark:text-slate-400 dark:hover:text-slate-200"
            >
              <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
            </button>
          </h1>
        )}
        <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
          {email ? (
            <a
              href={buildLeadMailto(email, "", "")}
              onClick={handleEmail}
              className="min-w-0 truncate hover:text-slate-900 hover:underline dark:hover:text-slate-100"
              title={email}
            >
              {email}
            </a>
          ) : null}
          {email && phone ? <span aria-hidden className="text-slate-300 dark:text-slate-600">•</span> : null}
          {phone ? (
            <a
              href={`tel:${phone.replace(/[\s()-]/g, "")}`}
              onClick={handleCall}
              className="shrink-0 tabular-nums hover:text-slate-900 hover:underline dark:hover:text-slate-100"
            >
              {phone}
            </a>
          ) : null}
          {!email && !phone ? (
            <span className="text-slate-500 dark:text-slate-400">No contact details yet</span>
          ) : null}
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {lead?.lead_status && (
            <Badge color="sky" size="sm" variant="chip">
              {lead.lead_status}
            </Badge>
          )}
          {compactJourneyStageLabel(client.journey_stage) && (
            <Badge
              color={journeyStageBadgeColor(client.journey_stage)}
              size="sm"
              variant="chip"
            >
              {compactJourneyStageLabel(client.journey_stage)}
            </Badge>
          )}
          {lead?.proposition && (
            <Badge color="zinc" size="sm" variant="chip">
              {lead.proposition}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <ContactButton
          testId="lead-call-button"
          label={calling ? "Calling…" : "Call lead"}
          icon={<FontAwesomeIcon icon={faPhone} className="h-3.5 w-3.5" />}
          href={phone ? `tel:${phone.replace(/[\s()-]/g, "")}` : undefined}
          title={phone || "No phone number on record"}
          onClick={handleCall}
          primary
        />
        <ContactButton
          testId="lead-email-button"
          label="Email lead"
          icon={<FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5" />}
          href={email ? buildLeadMailto(email, "", "") : undefined}
          title={email || "No email address on record"}
          onClick={email ? handleEmail : undefined}
        />
      </div>
      {email ? (
        <LeadEmailDialog
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          faClientId={faClientId}
          leadName={client.display_name}
          toEmail={email}
        />
      ) : null}
    </div>
  );
}

function ContactButton({
  testId,
  label,
  icon,
  href,
  title,
  onClick,
  primary,
}: {
  testId: string;
  label: string;
  icon: React.ReactNode;
  href: string | undefined;
  title: string;
  onClick?: (event: React.MouseEvent) => void;
  primary?: boolean;
}) {
  const className = clsx(
    "inline-flex h-9 shrink-0 items-center gap-2 rounded-xl px-3.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/40",
    !href && "cursor-not-allowed opacity-50",
    href &&
      (primary
        ? "bg-slate-900 text-white shadow-sm hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
        : "border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:text-slate-900 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-200 dark:hover:bg-white/[0.1] dark:hover:text-white"),
    !href &&
      "border border-slate-200 bg-transparent text-slate-400 dark:border-white/10 dark:text-slate-500",
  );
  if (!href) {
    return (
      <span data-testid={testId} title={title} aria-disabled="true" className={className}>
        {icon}
        {label}
      </span>
    );
  }
  return (
    <a data-testid={testId} href={href} title={title} className={className} onClick={onClick}>
      {icon}
      {label}
    </a>
  );
}

// ── Overview ─────────────────────────────────────────────────────────

function OverviewTab({
  client,
  faClientId,
  onChanged,
}: {
  client: FaClientDetail;
  faClientId: string;
  onChanged: () => void;
}) {
  const lead = client.lead;
  const [lostOpen, setLostOpen] = useState(false);
  const contact = (client.contact ?? {}) as Record<string, unknown>;
  const email = firstString(contact, ["email", "email_address"]);
  const phone = firstString(contact, ["mobile", "mobile_telephone", "phone"]);

  const saveField = async (patch: FaClientUpdate) => {
    await AgenticV2API.updateFaClient(faClientId, patch);
    onChanged();
  };

  const captureEntries = Object.entries(lead?.capture_fields ?? {}).filter(
    ([, value]) => value !== null && value !== undefined && String(value).trim() !== "",
  );

  const captureFields = (lead?.capture_fields ?? {}) as Record<string, unknown>;
  const leadType = firstString(captureFields, [
    "lead_type",
    "client_type",
  ]);
  const proposition = firstString(captureFields, ["proposition"]) || lead?.proposition?.trim() || "";
  const formattedValue =
    lead?.opportunity_value != null
      ? new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: "GBP",
          maximumFractionDigits: 0,
        }).format(lead.opportunity_value)
      : null;

  return (
    <div className="space-y-4">
      {/* At-a-glance strip — dictionary-backed facts only (no Rating: not a lead field). */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
        <StatTile
          icon={<FontAwesomeIcon icon={faSterlingSign} className="h-3.5 w-3.5" />}
          tint="bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300"
          label="Value"
          value={formattedValue ?? "Not set"}
          valueMuted={!formattedValue}
        />
        <StatTile
          icon={<FontAwesomeIcon icon={faUser} className="h-3.5 w-3.5" />}
          tint="bg-sky-500/10 text-sky-600 dark:bg-sky-400/10 dark:text-sky-300"
          label="Lead type"
          value={leadType || "Not set"}
          valueMuted={!leadType}
        />
        <StatTile
          icon={<FontAwesomeIcon icon={faFlag} className="h-3.5 w-3.5" />}
          tint="bg-violet-500/10 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300"
          label="Proposition"
          value={proposition || "Not set"}
          valueMuted={!proposition}
        />
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-5">
        <div className="space-y-4 xl:col-span-3">
          <Widget
            title="Contact"
            subtitle="Tap to call or email"
            icon={<FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5" />}
            tint="bg-sky-500/10 text-sky-600 dark:bg-sky-400/10 dark:text-sky-300"
          >
            <EditableRow
              label="Email"
              value={email}
              testId="lead-field-email"
              fieldKey="email"
              validate={validateEmail}
              onSave={saveField}
              href={email ? `mailto:${email}` : undefined}
            />
            <EditableRow
              label="Mobile"
              value={phone}
              testId="lead-field-mobile"
              fieldKey="mobile"
              inputType="tel"
              onSave={saveField}
              href={phone ? `tel:${phone.replace(/[\s()-]/g, "")}` : undefined}
            />
          </Widget>

          {client.summary && (
            <Widget
              title="About"
              icon={<FontAwesomeIcon icon={faCircleInfo} className="h-3.5 w-3.5" />}
              tint="bg-slate-500/10 text-slate-500 dark:bg-white/[0.06] dark:text-slate-300"
            >
              <p className="px-5 py-4 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                {client.summary}
              </p>
            </Widget>
          )}

          {captureEntries.length > 0 && (
            <Widget
              title="Capture"
              subtitle={`${captureEntries.length} field${captureEntries.length === 1 ? "" : "s"} from intake`}
              icon={<FontAwesomeIcon icon={faInbox} className="h-3.5 w-3.5" />}
              tint="bg-amber-500/10 text-amber-600 dark:bg-amber-400/10 dark:text-amber-300"
            >
              {captureEntries.map(([key, value]) => {
                const text = String(value);
                if (WORKFLOW_OWNED_CAPTURE_KEYS.has(key)) {
                  return (
                    <div
                      key={key}
                      className="flex items-baseline justify-between gap-4 px-5 py-3"
                    >
                      <dt className="min-w-0 shrink-0 text-[13px] text-slate-500 dark:text-slate-400">
                        {humanizeFieldName(key)}
                      </dt>
                      <dd
                        className="flex min-w-0 items-center justify-end gap-1.5 text-right"
                        title={text}
                      >
                        <span className="min-w-0 truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                          {text}
                        </span>
                        {/* Spacer matching the edit button so values align with editable rows. */}
                        <span aria-hidden className="w-6 shrink-0" />
                      </dd>
                    </div>
                  );
                }
                return (
                  <CaptureRow key={key} fieldKey={key} value={text} onSave={saveField} />
                );
              })}
            </Widget>
          )}
        </div>

        <div className="space-y-4 xl:col-span-2">
          <Widget
            title="Details"
            icon={<FontAwesomeIcon icon={faChartLine} className="h-3.5 w-3.5" />}
            tint="bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300"
          >
            <EditableRow
              label="Value"
              value={lead?.opportunity_value != null ? String(lead.opportunity_value) : ""}
              display={formattedValue ?? undefined}
              testId="lead-field-opportunity-value"
              fieldKey="opportunity_value"
              inputType="number"
              validate={validateOpportunity}
              onSave={saveField}
              strong
            />
            <EditableRow
              label="Source"
              value={lead?.lead_source?.trim() ?? ""}
              testId="lead-field-source"
              fieldKey="lead_source"
              onSave={saveField}
            />
            <EditableRow
              label="Proposition"
              value={lead?.proposition?.trim() ?? ""}
              testId="lead-field-proposition"
              fieldKey="proposition"
              onSave={saveField}
            />
          </Widget>

          <Widget
            title="Pipeline"
            subtitle="Workflow owned"
            icon={<FontAwesomeIcon icon={faFlag} className="h-3.5 w-3.5" />}
            tint="bg-violet-500/10 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300"
          >
            <div className="flex items-center justify-between gap-4 px-5 py-3.5">
              <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                Status
              </dt>
              <dd className="flex min-w-0 items-center justify-end gap-1.5">
                {lead?.lead_status ? (
                  <Badge color="sky" size="sm" variant="chip">
                    {lead.lead_status}
                  </Badge>
                ) : (
                  <EmptyValue label="No status" />
                )}
                {lead?.lead_lost_reason ? (
                  <Badge color="red" size="sm" variant="chip" data-testid="lead-lost-reason-badge">
                    {lead.lead_lost_reason}
                  </Badge>
                ) : null}
              </dd>
            </div>
            {lead?.lead_lost_detail ? (
              <div className="flex items-baseline justify-between gap-4 px-5 py-3.5" data-testid="lead-lost-detail-row">
                <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  Loss detail
                </dt>
                <dd className="min-w-0 text-right text-sm text-slate-700 dark:text-slate-200">
                  {lead.lead_lost_detail}
                </dd>
              </div>
            ) : null}
            {lead?.contact_permission ? (
              <div className="flex items-center justify-between gap-4 px-5 py-3.5" data-testid="lead-contact-permission-row">
                <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  Contact permission
                </dt>
                <dd className="min-w-0 text-right text-sm font-medium text-slate-900 dark:text-slate-100">
                  {lead.contact_permission === "follow_up"
                    ? "Follow up in future"
                    : lead.contact_permission === "keep_in_touch"
                      ? "Keep in touch"
                      : lead.contact_permission === "do_not_contact"
                        ? "Do not contact"
                        : lead.contact_permission}
                </dd>
              </div>
            ) : null}
            {lead?.followup_date ? (
              <div className="flex items-center justify-between gap-4 px-5 py-3.5" data-testid="lead-followup-date-row">
                <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  Follow-up date
                </dt>
                <dd className="min-w-0 text-right text-sm font-medium text-slate-900 dark:text-slate-100">
                  {lead.followup_date}
                  {lead.followup_reason ? ` — ${lead.followup_reason}` : ""}
                </dd>
              </div>
            ) : null}
            {lead?.reactivated_at ? (
              <div className="flex items-baseline justify-between gap-4 px-5 py-3.5" data-testid="lead-reinstated-row">
                <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  Reinstated
                </dt>
                <dd className="min-w-0 text-right text-sm text-emerald-700 dark:text-emerald-300">
                  {lead.reactivation_reason || "Reinstated to Contacting"}
                </dd>
              </div>
            ) : null}
            {lead?.lead_status === "Lost" ? (
              <div className="flex items-center justify-end gap-2 px-5 py-3.5" data-testid="lead-reinstate-row">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="lead-reinstate-button"
                  onClick={() => {
                    void AgenticV2API.reinstateLostRecord(faClientId, {}).then(() => onChanged());
                  }}
                >
                  Reinstate lead
                </Button>
              </div>
            ) : lead ? (
              <div className="flex items-center justify-end gap-2 px-5 py-3.5" data-testid="lead-mark-lost-row">
                <Button size="sm" outline data-testid="lead-mark-lost-button" onClick={() => setLostOpen(true)}>
                  Mark as lost
                </Button>
                <RecordLostDialog
                  open={lostOpen}
                  onClose={() => setLostOpen(false)}
                  onRecorded={onChanged}
                  faClientId={faClientId}
                  leadName={client.display_name}
                />
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-4 px-5 py-3.5">
              <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                Adviser
              </dt>
              <dd className="flex min-w-0 items-center justify-end gap-2">
                {lead?.adviser_name?.trim() ? (
                  <>
                    <span className="min-w-0 truncate text-right">
                      <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                        {lead.adviser_name.trim()}
                      </span>
                    </span>
                    <ClientAvatar name={lead.adviser_name.trim()} size="xs" />
                  </>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                    Unassigned
                  </span>
                )}
              </dd>
            </div>
          </Widget>
        </div>
      </div>
    </div>
  );
}

/** Elevated profile card with icon-tile header and strong dark-mode contrast. */
function Widget({
  title,
  subtitle,
  icon,
  tint,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  tint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_-12px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-white/[0.05] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_12px_32px_-16px_rgba(0,0,0,0.7)]">
      <header className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/60 px-5 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-slate-950/[0.06] dark:ring-white/10 ${tint}`}
        >
          {icon}
        </span>
        <div className="min-w-0">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300">
            {title}
          </h2>
          {subtitle ? (
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
          ) : null}
        </div>
      </header>
      <dl className="divide-y divide-slate-100 dark:divide-white/[0.06]">{children}</dl>
    </section>
  );
}

/** Compact KPI tile for the at-a-glance strip. */
function StatTile({
  icon,
  tint,
  label,
  value,
  valueMuted,
  badge,
}: {
  icon: React.ReactNode;
  tint: string;
  label: string;
  value: string;
  valueMuted?: boolean;
  badge?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-white/[0.05] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]">
      <div className="flex items-center gap-2">
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ring-slate-950/[0.06] dark:ring-white/10 ${tint}`}
        >
          {icon}
        </span>
        <p className="truncate text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
          {label}
        </p>
      </div>
      <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
        {badge ?? (
          <p
            className={`min-w-0 truncate text-[15px] font-semibold tracking-tight tabular-nums ${
              valueMuted
                ? "font-medium text-slate-500 dark:text-slate-400"
                : "text-slate-900 dark:text-white"
            }`}
          >
            {value}
          </p>
        )}
      </div>
    </div>
  );
}

function validateEmail(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) ? null : "Enter a valid email address.";
}

function validateOpportunity(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const amount = Number(trimmed);
  if (Number.isNaN(amount) || amount < 0) return "Value must be 0 or more.";
  return null;
}

function validateName(value: string): string | null {
  return value.trim() === "" ? "Name must not be blank." : null;
}

/** Muted empty copy — never a bare dash. */
function EmptyValue({ label = "Not provided" }: { label?: string }) {
  return (
    <span className="text-sm font-normal text-slate-500 dark:text-slate-400">{label}</span>
  );
}

function EditableRow({
  label,
  value,
  display,
  testId,
  fieldKey,
  inputType,
  validate,
  onSave,
  href,
  strong,
}: {
  label: string;
  value: string;
  display?: string;
  testId: string;
  fieldKey: "display_name" | "email" | "mobile" | "opportunity_value" | "lead_source" | "proposition";
  inputType?: string;
  validate?: (value: string) => string | null;
  onSave: (patch: FaClientUpdate) => Promise<void>;
  href?: string;
  strong?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const shown = display ?? value.trim();
  const isEmpty = value.trim() === "";

  const startEdit = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    const validationError = validate?.(draft) ?? null;
    if (validationError) {
      setError(validationError);
      return;
    }
    if (draft.trim() === value.trim() && fieldKey !== "opportunity_value") {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const patch: FaClientUpdate =
        fieldKey === "opportunity_value"
          ? { opportunity_value: draft.trim() === "" ? undefined : Number(draft) }
          : { [fieldKey]: draft.trim() };
      await onSave(patch);
      setEditing(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't save this field.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 py-3.5 transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]">
      <div className="group flex items-center justify-between gap-4">
        <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
          {label}
        </dt>
        {editing ? (
          <dd className="min-w-0 flex-1">
            <FormInput
              value={draft}
              type={inputType}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void handleSave();
                if (event.key === "Escape") setEditing(false);
              }}
              disabled={saving}
              aria-label={label}
              data-testid={`${testId}-input`}
              autoFocus
            />
          </dd>
        ) : (
          <dd
            className="flex min-w-0 items-center justify-end gap-1.5 text-right"
            title={isEmpty ? undefined : shown}
            data-testid={`${testId}-value`}
          >
            {isEmpty ? (
              <button
                type="button"
                onClick={startEdit}
                data-testid={`${testId}-edit`}
                aria-label={`Add ${label}`}
                title={`Add ${label}`}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-400/10 dark:hover:text-indigo-200"
              >
                <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                Add {label.toLowerCase()}
              </button>
            ) : (
              <>
                {href ? (
                  <a
                    href={href}
                    className={`min-w-0 truncate text-sm hover:underline ${
                      strong
                        ? "font-semibold tabular-nums text-slate-900 dark:text-white"
                        : "font-medium text-slate-900 dark:text-slate-100"
                    }`}
                  >
                    {shown}
                  </a>
                ) : (
                  <span
                    className={`min-w-0 truncate text-sm ${
                      strong
                        ? "font-semibold tabular-nums text-slate-900 dark:text-white"
                        : "font-medium text-slate-900 dark:text-slate-100"
                    }`}
                  >
                    {shown}
                  </span>
                )}
                <button
                  type="button"
                  onClick={startEdit}
                  aria-label={`Edit ${label}`}
                  data-testid={`${testId}-edit`}
                  title={`Edit ${label}`}
                  className="shrink-0 rounded-md p-1.5 text-slate-500 opacity-0 transition-all hover:bg-slate-100 hover:text-slate-700 focus:opacity-100 focus:outline-none group-hover:opacity-100 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-200"
                >
                  <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
                </button>
              </>
            )}
          </dd>
        )}
      </div>
      {editing ? (
        <div className="mt-2 flex items-center justify-end gap-2">
          {error ? (
            <p className="mr-auto text-xs text-red-600 dark:text-red-400" data-testid={`${testId}-error`}>
              {error}
            </p>
          ) : null}
          <Button outline size="sm" onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={saving} loading={saving} data-testid={`${testId}-save`}>
            Save
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Workflow-owned capture keys stay read-only (same rule as the PATCH endpoint). */
const WORKFLOW_OWNED_CAPTURE_KEYS = new Set(["lead_status", "lead_lost_reason", "adviser_id", "adviser_name"]);

/** Editable intake capture row — saves via { capture_fields: { [fieldKey]: draft } }. */
function CaptureRow({
  fieldKey,
  value,
  onSave,
}: {
  fieldKey: string;
  value: string;
  onSave: (patch: FaClientUpdate) => Promise<void>;
}) {
  const label = humanizeFieldName(fieldKey);
  const testId = `lead-capture-${fieldKey}`;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const handleSave = async () => {
    if (draft.trim() === value.trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ capture_fields: { [fieldKey]: draft.trim() } });
      setEditing(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't save this field.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 py-3 transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]">
      <div className="group flex items-center justify-between gap-4">
        <dt className="min-w-0 shrink-0 text-[13px] text-slate-500 dark:text-slate-400">
          {label}
        </dt>
        {editing ? (
          <dd className="min-w-0 flex-1">
            <FormInput
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void handleSave();
                if (event.key === "Escape") setEditing(false);
              }}
              disabled={saving}
              aria-label={label}
              data-testid={`${testId}-input`}
              autoFocus
            />
          </dd>
        ) : (
          <dd
            className="flex min-w-0 items-center justify-end gap-1.5 text-right"
            title={value}
            data-testid={`${testId}-value`}
          >
            <span className="min-w-0 truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {value}
            </span>
            <button
              type="button"
              onClick={() => {
                setDraft(value);
                setError(null);
                setEditing(true);
              }}
              aria-label={`Edit ${label}`}
              data-testid={`${testId}-edit`}
              title={`Edit ${label}`}
              className="shrink-0 rounded-md p-1.5 text-slate-500 opacity-0 transition-all hover:bg-slate-100 hover:text-slate-700 focus:opacity-100 focus:outline-none group-hover:opacity-100 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-200"
            >
              <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
            </button>
          </dd>
        )}
      </div>
      {editing ? (
        <div className="mt-2 flex items-center justify-end gap-2">
          {error ? (
            <p className="mr-auto text-xs text-red-600 dark:text-red-400" data-testid={`${testId}-error`}>
              {error}
            </p>
          ) : null}
          <Button outline size="sm" onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={saving} loading={saving} data-testid={`${testId}-save`}>
            Save
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function firstString(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function humanizeFieldName(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

// ── Tasks ────────────────────────────────────────────────────────────

function TasksTab({
  tasks,
  loading,
  visibility,
  onVisibilityChange,
}: {
  tasks: TaskActionItem[];
  loading: boolean;
  visibility: VisibilityScope;
  onVisibilityChange: (v: VisibilityScope) => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {loading
            ? "Loading tasks..."
            : tasks.length === 0
              ? "No tasks for this lead yet."
              : `${tasks.length} task${tasks.length === 1 ? "" : "s"}`}
        </p>
        <VisibilityFilter value={visibility} onChange={onVisibilityChange} />
      </div>
      {loading && tasks.length === 0 ? null : (
        <TaskReviewTable tasks={tasks} loading={loading} emptyMessage="No tasks for this lead yet." testId="lead-task-console-table" />
      )}
    </div>
  );
}

// ── Error state ──────────────────────────────────────────────────────

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-slate-600 dark:text-slate-300">
      <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-amber-500" />
      <p className="text-sm">{message}</p>
      <Button onClick={onRetry}>Retry</Button>
    </div>
  );
}
